import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { Icon } from "@/ds";

/**
 * A FixyBee's payment CONTACT handles, as a client sees them (T12-M4b/M4c).
 *
 * Two actions and no others: copy the handle, or open the app when a URL for it
 * exists. The payment happens inside that app — FixyDany processes nothing,
 * records nothing and knows nothing about whether it happened, which is why
 * there is no amount, no status and no "mark as paid" anywhere here.
 *
 * Zelle and Apple Cash carry no link because none exists: Zelle is a phone or
 * an email inside someone's bank app, and Apple Cash lives in Messages. For
 * those two, copy is the only honest action, so it is the only one offered.
 *
 * No third-party logos, by instruction — the 32px circle is the app's initial.
 */
export default function PaymentHandleList({ methods = [], compact = false }) {
  const { t } = useTranslation();
  const [copied, setCopied] = useState(null);

  if (!methods.length) return null;

  const copy = async (m) => {
    try {
      await navigator.clipboard.writeText(m.handle);
    } catch {
      // Clipboard is blocked outside a secure context and in some in-app
      // browsers. The handle is on screen either way, which is the fallback.
      return;
    }
    setCopied(m.app_key);
    window.setTimeout(() => setCopied((k) => (k === m.app_key ? null : k)), 1600);
  };

  return (
    <div
      style={{
        background: "var(--paper)", border: "1px solid var(--sand)",
        borderRadius: 16, padding: "0 12px",
      }}
      data-testid="payment-handles"
    >
      {methods.map((m, i) => (
        <div
          key={m.app_key}
          style={{
            minHeight: 60, display: "flex", alignItems: "center", gap: 14,
            padding: compact ? "8px 4px" : "10px 4px",
            borderBottom: i === methods.length - 1 ? "none" : "1px solid var(--linen)",
            flexWrap: "wrap",
          }}
          data-testid={`payment-handle-${m.app_key}`}
        >
          <span
            aria-hidden="true"
            style={{
              width: 32, height: 32, borderRadius: "50%", flex: "none",
              background: "var(--linen)", border: "1px solid var(--sand)",
              display: "flex", alignItems: "center", justifyContent: "center",
              font: "700 13px var(--font-mono)", color: "var(--worker)",
            }}
          >
            {m.initial}
          </span>
          <span style={{ display: "grid", gap: 1, minWidth: 0, flex: 1 }}>
            <span style={{ font: "600 16px var(--font-display)", color: "var(--text-body)" }}>
              {m.label}
            </span>
            <span
              style={{
                font: "500 12px var(--font-mono)", color: "var(--smoke)",
                overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
              }}
            >
              {m.handle}
            </span>
          </span>

          <span style={{ display: "flex", alignItems: "center", gap: 4, marginLeft: "auto" }}>
            <button
              type="button"
              onClick={() => copy(m)}
              aria-label={t("payouts.copyHandle", { app: m.label })}
              data-testid={`payment-copy-${m.app_key}`}
              style={{
                minHeight: 44, display: "inline-flex", alignItems: "center", gap: 6,
                padding: "0 10px", borderRadius: 8, border: "1px solid var(--sand)",
                background: "transparent", cursor: "pointer",
                font: "600 14px var(--font-display)", color: "var(--text-body)",
              }}
            >
              <Icon name={copied === m.app_key ? "check" : "copy"} size={16} />
              {copied === m.app_key ? t("payouts.copied") : t("payouts.copy")}
            </button>
            {m.link && (
              <a
                href={m.link}
                target="_blank"
                rel="noopener noreferrer"
                aria-label={t("payouts.openIn", { app: m.label })}
                data-testid={`payment-open-${m.app_key}`}
                style={{
                  width: 44, height: 44, display: "inline-flex", alignItems: "center",
                  justifyContent: "center", color: "var(--smoke)", textDecoration: "none",
                }}
              >
                <Icon name="external-link" size={18} />
              </a>
            )}
          </span>
        </div>
      ))}
    </div>
  );
}
